/* One generated Veo variant in the review strip. Owns its own Save /
   Regenerate / prompt-editor state so a single failed ratio never blocks the
   ratios that worked. */
import { useEffect, useState } from "react"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import type { PendingResult } from "./types"

interface PendingResultCardProps {
  result: PendingResult
  /** Prompt to seed the per-card editor with — usually the main panel prompt. */
  defaultPrompt: string
  onSave: () => void
  /** Called with the prompt the user wants to regenerate with. Empty/undefined
   *  means fall back to the panel default. */
  onRegenerate: (promptOverride?: string) => void
}

export default function PendingResultCard({
  result,
  defaultPrompt,
  onSave,
  onRegenerate,
}: PendingResultCardProps) {
  const { ratio, status, previewUrl, error } = result
  const isWorking = status === "generating" || status === "saving"
  const [isEditingPrompt, setIsEditingPrompt] = useState(false)
  const [draftPrompt, setDraftPrompt] = useState(defaultPrompt)

  // Reset the draft whenever the panel default changes while the editor is
  // closed — opening fresh should always start from the current main prompt.
  useEffect(() => {
    if (!isEditingPrompt) setDraftPrompt(defaultPrompt)
  }, [defaultPrompt, isEditingPrompt])

  const handleRegenerateClick = () => {
    if (isWorking) return
    onRegenerate(isEditingPrompt ? draftPrompt : undefined)
    setIsEditingPrompt(false)
  }
  return (
    <div className="rounded-lg border border-system-gray-800 bg-system-gray-900/40 p-2 space-y-2">
      {/* Player — each card uses its native ratio so 9:16 renders portrait
          and 16:9 renders landscape side by side. */}
      <div
        className="w-full rounded-md overflow-hidden bg-black ring-1 ring-system-gray-700/50 flex items-center justify-center"
        style={{ aspectRatio: ratio.replace(":", "/") }}
      >
        {previewUrl ? (
          <video
            src={previewUrl}
            controls
            playsInline
            preload="metadata"
            className="w-full h-full object-contain"
          />
        ) : status === "failed" ? (
          <XMarkIcon className="w-8 h-8 text-system-error-500/70" />
        ) : (
          <span className="loading loading-spinner loading-md text-brand" />
        )}
      </div>
      {/* Metadata + actions */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[11px] font-semibold text-white">{ratio}</span>
          <span
            className={cn(
              "text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded",
              status === "saved" &&
                "bg-system-success-500/15 text-system-success-500",
              status === "failed" &&
                "bg-system-error-500/15 text-system-error-500",
              status === "generating" && "bg-brand/15 text-brand",
              status === "saving" && "bg-brand/15 text-brand",
              status === "success" && "bg-system-gray-800 text-system-gray-300",
            )}
          >
            {status === "saved"
              ? "Saved"
              : status === "failed"
                ? "Failed"
                : status === "generating"
                  ? "Generating…"
                  : status === "saving"
                    ? "Saving…"
                    : "Ready"}
          </span>
          {status === "saved" && (
            <span className="text-[10px] text-system-gray-500 truncate">
              Added to your pool.
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {status !== "saved" && !isEditingPrompt && (
            <>
              <button
                onClick={() => setIsEditingPrompt(true)}
                disabled={isWorking}
                className="btn btn-xs btn-ghost text-system-gray-400 hover:text-white disabled:opacity-50"
                title="Edit prompt for this variant"
              >
                Edit prompt
              </button>
              <button
                onClick={handleRegenerateClick}
                disabled={isWorking}
                className="btn btn-xs btn-ghost text-system-gray-300 hover:text-white disabled:opacity-50"
              >
                Regenerate
              </button>
            </>
          )}
          {status === "success" && !isEditingPrompt && (
            <button
              onClick={onSave}
              className="btn btn-xs bg-brand text-white hover:bg-brand/90"
            >
              Save
            </button>
          )}
        </div>
      </div>
      {status === "failed" && error && (
        <p className="text-[10px] text-system-error-500 line-clamp-2">
          {error}
        </p>
      )}
      {isEditingPrompt && (
        <div className="space-y-1.5 rounded-md border border-system-gray-700/60 bg-system-gray-900/60 p-2">
          <span className="text-[9px] uppercase tracking-wide text-system-gray-500">
            Custom prompt for {ratio}
          </span>
          <textarea
            value={draftPrompt}
            onChange={e => setDraftPrompt(e.target.value)}
            disabled={isWorking}
            rows={3}
            className="textarea textarea-xs w-full bg-system-gray-800 border-system-gray-700 text-white text-[11px] focus:border-brand focus:outline-none disabled:opacity-50"
            placeholder="Describe the motion you want for this variant…"
          />
          <div className="flex items-center justify-end gap-1.5">
            <button
              onClick={() => {
                setIsEditingPrompt(false)
                setDraftPrompt(defaultPrompt)
              }}
              disabled={isWorking}
              className="btn btn-xs btn-ghost text-system-gray-400 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleRegenerateClick}
              disabled={isWorking || !draftPrompt.trim()}
              className="btn btn-xs bg-brand text-white hover:bg-brand/90 disabled:opacity-50"
            >
              Regenerate
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
